import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DollarSign, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';

interface BillingRates {
  state_billing_rate: number;
  caregiver_rate: number;
}

export function DefaultBillingRatesSettings() {
  const [rates, setRates] = useState<BillingRates>({
    state_billing_rate: 21.52,
    caregiver_rate: 18.25
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('setting_value')
        .eq('setting_key', 'default_billing_rates')
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data?.setting_value) {
        const value = data.setting_value as unknown as BillingRates;
        setRates({
          state_billing_rate: value.state_billing_rate ?? 21.52,
          caregiver_rate: value.caregiver_rate ?? 18.25
        });
      }
    } catch (error) {
      console.error('Error loading default billing rates:', error);
      toast.error('Failed to load default billing rates');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Check if setting exists first
      const { data: existing } = await supabase
        .from('platform_settings')
        .select('id')
        .eq('setting_key', 'default_billing_rates')
        .single();

      if (existing) {
        // Update existing
        const { error } = await supabase
          .from('platform_settings')
          .update({
            setting_value: rates as unknown as Json,
            description: 'Default billing rates used when agency-specific rates are not configured'
          })
          .eq('setting_key', 'default_billing_rates');

        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('platform_settings')
          .insert([{
            setting_key: 'default_billing_rates',
            setting_value: rates as unknown as Json,
            description: 'Default billing rates used when agency-specific rates are not configured'
          }]);

        if (error) throw error;
      }

      toast.success('Default billing rates updated successfully');
    } catch (error) {
      console.error('Error saving default billing rates:', error);
      toast.error('Failed to save default billing rates');
    } finally {
      setSaving(false);
    }
  };

  const marginPerHour = rates.state_billing_rate - rates.caregiver_rate;

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-primary" />
          Default Billing Rates
        </CardTitle>
        <CardDescription>
          These rates are used for billing calculations when agencies don't have their own rates configured, 
          or when viewing aggregated analytics across all agencies.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="state_billing_rate">State Reimbursement Rate ($/hr)</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="state_billing_rate"
                type="number"
                step="0.01"
                min="0"
                value={rates.state_billing_rate}
                onChange={(e) => setRates(prev => ({ 
                  ...prev, 
                  state_billing_rate: parseFloat(e.target.value) || 0 
                }))}
                className="pl-9"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              The hourly rate agencies receive from state/Medicare for patient care
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="caregiver_rate">Caregiver Rate ($/hr)</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="caregiver_rate"
                type="number"
                step="0.01"
                min="0"
                value={rates.caregiver_rate}
                onChange={(e) => setRates(prev => ({ 
                  ...prev, 
                  caregiver_rate: parseFloat(e.target.value) || 0 
                }))}
                className="pl-9"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              The hourly cost agencies pay to caregivers
            </p>
          </div>
        </div>

        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">Calculated Margin per Hour</p>
              <p className="text-xs text-muted-foreground">State Rate - Caregiver Rate</p>
            </div>
            <div className="text-right">
              <p className={`text-xl font-bold ${marginPerHour >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                ${marginPerHour.toFixed(2)}/hr
              </p>
              <p className="text-xs text-muted-foreground">
                {marginPerHour >= 0 ? 'Profit margin' : 'Loss margin'}
              </p>
            </div>
          </div>
        </div>

        <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto">
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Default Rates
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}